import React, { useState, useEffect } from "react";
import axios from "axios";
import { config } from "../../Helpers/env";
import { LoadingGif } from "../Utils/Loader";
import MainSnackbar from "../Utils/Snackbar/SmsSnackbar";
import { Container } from "../Utils/styledComponents";
import PageTitle from "../Utils/smsTitle";
import { SubHeader } from "../../Containers/SubHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faRocket, 
  faHourglassHalf, 
  faSkullCrossbones, 
  faPills,
  faExclamationTriangle,
  faFileExcel,
  faFilePdf,
  faDollarSign,
  faCoins,
  faChartLine
} from "@fortawesome/free-solid-svg-icons";
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../actions/snackbarActions";

const BackButton = ({ to, text }) => (
  <Link to={to}>
    <button className="btn btn-sm sms-btn px-4 py-2">
      <ArrowBackIcon fontSize="small" className="me-2" />
      Back to {text}
    </button>
  </Link>
);

const StockMovementReport = ({ actions }) => {
  const today = new Date().toISOString().slice(0, 10);
  const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

  const [filters, setFilters] = useState({
    start_date: firstDayOfMonth,
    end_date: today,
  });

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState("top");

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);

      const res = await axios.get(`${config.smsUrl}/creport/stock_movement_report`, { params });
      setReport(res.data);
      actions.snackbarActions.successSnackbar?.("Stock Movement Report Loaded!");
    } catch (err) {
      actions.snackbarActions.errorSnackbar?.("Failed to load report");
      console.error(err);
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [filters.start_date, filters.end_date]);

  // NEW BACKEND KEYS – UPDATED HERE
  const {
    summary = {},
    top_selling_products = [],
    medium_moving_products = [],
    dead_stock_products = [],
    all_data = []
  } = report || {};

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : "-";

  const formatSSP = (amount) => `SSP ${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const formatUSD = (amount) => `$${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Updated to use new fields from backend
  const totalSSP = summary.total_revenue_ssp || 0;
  const totalUSD = summary.total_revenue_usd || 0;

  return (
    <>
      <PageTitle title="Stock Movement Report" />
      <SubHeader title="Reports" subTitle="Stock Movement Analysis • SSP + USD">
        <FontAwesomeIcon icon={faPills} />
      </SubHeader>

      <Container className="mt-1">
        <MainSnackbar />

        {/* FILTER BAR */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body py-3">
            <div className="row align-items-center g-3">
              <div className="col-auto">
                <BackButton to="/reports/generalreports" text="Reports" />
              </div>
              <div className="col-auto">
                <input type="date" className="form-control form-control-sm" value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
              </div>
              <div className="col-auto">
                <input type="date" className="form-control form-control-sm" value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
              </div>
              <div className="col-auto">
                <button onClick={fetchReport} disabled={loading} className="btn btn-primary btn-sm px-4">
                  {loading ? "Loading..." : "Refresh"}
                </button>
              </div>
              <div className="col ms-auto text-end">
                <button className="btn btn-success btn-sm me-2"><FontAwesomeIcon icon={faFileExcel} /> Excel</button>
                <button className="btn btn-danger btn-sm"><FontAwesomeIcon icon={faFilePdf} /> PDF</button>
              </div>
            </div>
          </div>
        </div>

        {/* BEAUTIFUL SUMMARY SECTION – SAME DESIGN */}
        <div className="row g-4 mb-5">
          {/* Top Selling */}
          <div className="col-lg-3 col-md-6">
            <div className="card h-100 shadow-lg border-0 rounded-4 overflow-hidden" style={{ background: "linear-gradient(135deg, #11998e, #38ef7d)" }}>
              <div className="card-body text-white text-center p-4">
                <FontAwesomeIcon icon={faRocket} size="3x" className="mb-3 opacity-90" />
                <h5 className="fw-bold">Top Selling</h5>
                <h2 className="display-5 fw-bold mb-0">{summary.top_selling || 0}</h2>
                <small>Products</small>
              </div>
            </div>
          </div>

          {/* Medium Moving */}
          <div className="col-lg-3 col-md-6">
            <div className="card h-100 shadow-lg border-0 rounded-4 overflow-hidden" style={{ background: "linear-gradient(135deg, #ffa726, #fb8c00)" }}>
              <div className="card-body text-white text-center p-4">
                <FontAwesomeIcon icon={faHourglassHalf} size="3x" className="mb-3 opacity-90" />
                <h5 className="fw-bold">Medium Moving</h5>
                <h2 className="display-5 fw-bold mb-0">{summary.medium_moving || 0}</h2>
                <small>Products</small>
              </div>
            </div>
          </div>

          {/* Dead Stock */}
          <div className="col-lg-3 col-md-6">
            <div className="card h-100 shadow-lg border-0 rounded-4 overflow-hidden" style={{ background: "linear-gradient(135deg, #e74c3c, #c0392b)" }}>
              <div className="card-body text-white text-center p-4">
                <FontAwesomeIcon icon={faSkullCrossbones} size="3x" className="mb-3 opacity-90" />
                <h5 className="fw-bold">Dead Stock</h5>
                <h2 className="display-5 fw-bold mb-0">{summary.dead_stock || 0}</h2>
                {summary.expired_in_dead_stock > 0 && (
                  <div className="mt-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>{summary.expired_in_dead_stock} Expired</strong>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* TOTAL REVENUE CARD – NOW SHOWS CORRECT SSP + USD */}
          <div className="col-lg-3 col-md-6">
            <div className="card h-100 shadow-lg border-0 rounded-4" style={{ background: "linear-gradient(135deg, #667eea, #764ba2)" }}>
              <div className="card-body text-white p-4">
                <div className="text-center mb-4">
                  <FontAwesomeIcon icon={faChartLine} size="3x" className="opacity-90" />
                  <h5 className="fw-bold mt-3">Total Revenue</h5>
                </div>
                <div className="row text-center">
                  <div className="col-12 mb-3">
                    <div className="bg-black bg-opacity-10 rounded-3 py-3 px-4">
                      <div className="small opacity-80">South Sudanese Pound</div>
                      <div className="display-6 fw-bold">
                        <FontAwesomeIcon icon={faCoins} className="me-2" />
                        {formatSSP(totalSSP)}
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="bg-green bg-opacity-10 rounded-3 py-3 px-4">
                      <div className="small opacity-80">US Dollar</div>
                      <div className="display-6 fw-bold text-warning">
                        <FontAwesomeIcon icon={faDollarSign} className="me-2" />
                        {formatUSD(totalUSD)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TABS – SAME */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button className={`nav-link ${activeTab === "top" ? "active" : ""}`} onClick={() => setActiveTab("top")}>
              Top Selling ({top_selling_products.length})
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link ${activeTab === "medium" ? "active" : ""}`} onClick={() => setActiveTab("medium")}>
              Medium Moving ({medium_moving_products.length})
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link ${activeTab === "dead" ? "active" : ""}`} onClick={() => setActiveTab("dead")}>
              Dead Stock ({dead_stock_products.length})
            </button>
          </li>
        </ul>

        {loading && <div className="text-center py-5"><LoadingGif /></div>}

        {/* TOP SELLING TABLE – UPDATED KEYS */}
        {activeTab === "top" && top_selling_products.length > 0 && (
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead className="table-success text-dark">
                <tr>
                  <th>#</th>
                  <th>Product</th>
                  <th>Generic</th>
                  <th>Qty Sold</th>
                  <th>Times Sold</th>
                  <th className="text-center">Revenue (SSP)</th>
                  <th className="text-center">Revenue (USD)</th>
                  <th>Avg/Day</th>
                  <th>Last Sale</th>
                  <th>Stock</th>
                </tr>
              </thead>
              <tbody>
                {top_selling_products.map((item, i) => (
                  <tr key={i}>
                    <td><strong>{i + 1}</strong></td>
                    <td><strong>{item.product_name}</strong></td>
                    <td className="text-muted small">{item.generic_name || "-"}</td>
                    <td className="text-end fw-bold text-success">{item.qty_sold}</td>
                    <td className="text-center">{item.times_sold}</td>
                    <td className="text-end text-primary fw-bold">{formatSSP(item.revenue_ssp)}</td>
                    <td className="text-end text-success fw-bold">{formatUSD(item.revenue_usd)}</td>
                    <td className="text-center">{Number(item.avg_daily_sale).toFixed(2)}</td>
                    <td>{formatDate(item.last_sale_date)}</td>
                    <td className="text-end">{item.current_stock}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* MEDIUM MOVING – SAME UI, NEW KEYS */}
        {activeTab === "medium" && medium_moving_products.length > 0 && (
          <div className="row g-4">
            {medium_moving_products.map((item, i) => (
              <div key={i} className="col-lg-4 col-md-6">
                <div className="card border-warning shadow-sm h-100">
                  <div className="card-body">
                    <h6 className="fw-bold">{item.product_name}</h6>
                    <small className="text-muted">{item.generic_name || "—"}</small>
                    <hr />
                    <div className="small">
                      <div><strong>Qty Sold:</strong> {item.qty_sold}</div>
                      <div><strong>Revenue:</strong> {formatSSP(item.revenue_ssp)} → {formatUSD(item.revenue_usd)}</div>
                      <div><strong>Last Sale:</strong> {formatDate(item.last_sale_date)}</div>
                      <div><strong>Stock:</strong> {item.current_stock} {item.expired_qty > 0 && <span className="text-danger">(Expired: {item.expired_qty})</span>}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* DEAD STOCK – SAME UI, NEW KEYS */}
        {activeTab === "dead" && dead_stock_products.length > 0 && (
          <div className="row g-4">
            {dead_stock_products.map((item, i) => (
              <div key={i} className="col-lg-4 col-md-6">
                <div className="card border-danger shadow-lg text-white h-100" style={{ background: "linear-gradient(135deg, #c0392b, #e74c3c)" }}>
                  <div className="card-body">
                    <h6 className="fw-bold">
                      <FontAwesomeIcon icon={faSkullCrossbones} className="me-2" />
                      {item.product_name}
                    </h6>
                    <small>{item.generic_name || "—"}</small>
                    <hr className="border-light opacity-50" />
                    <div className="small">
                      <div className="fw-bold text-warning">
                        No sale in {item.days_inactive === 99999 ? "Ever" : `${item.days_inactive} days`}
                      </div>
                      <div>Stock: <strong>{item.current_stock}</strong></div>
                      {item.expired_qty > 0 && (
                        <div className="mt-2">
                          <FontAwesomeIcon icon={faExclamationTriangle} /> <strong>EXPIRED: {item.expired_qty}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && report && all_data.length === 0 && (
          <div className="text-center py-5">
            <h5 className="text-muted">No products found for the selected period</h5>
          </div>
        )}
      </Container>
    </>
  );
};

const mapDispatchToProps = dispatch => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) }
});

export default connect(null, mapDispatchToProps)(StockMovementReport);